import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  IAPIFetchResponse,
  IAPIListRequestOptions,
  IAPIListResponse
} from '@sondermind/utilities/models-http';
import { SessionType } from '@sondermind/utilities/models-session';

import { DirectScheduleSessionRequest, IDirectScheduleSessionRequest } from '../models/direct-schedule';
import { DirectScheduleEvent, IDirectScheduleEventResponse } from '../models/direct-schedule-event';

@Injectable({
  providedIn: 'root'
})
export class DirectScheduleSessionHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
  ) {}

  get indexUrl(): string {
    return `${this.configurationService.env.svcBase}/scheduling/direct_scheduled_sessions`;
  }

  list(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<DirectScheduleEvent>> {
    const params = this.http.generateListParams(opts);
    return this.http
      .get<IAPIListResponse<IDirectScheduleEventResponse>>(this.indexUrl, params)
      .pipe(
        map((resp) => ({ ...resp, data: resp?.data?.map((sr) => new DirectScheduleEvent(sr)) ?? [] })),
        this.http.handleHttpError(opts.error)
      );
  }

  submitDirectScheduleSession({
    locationId,
    personaProviderId,
    startTime,
    endTime,
    sessionType,
    clientGoals = null,
    clientAbout = null,
    timeProposal = false,
    personaClientId,
    clientReferralMemberId,
    opts = {}
  }: {
    locationId?: number;
    personaProviderId: number;
    startTime: DateTime;
    endTime: DateTime;
    sessionType: string;
    clientGoals?: string;
    clientAbout?: string;
    timeProposal?: boolean;
    personaClientId?: number;
    clientReferralMemberId?: number;
    opts?: Partial<IAPIListRequestOptions>;
  }): Observable<DirectScheduleSessionRequest | null> {
    const params = this.http.generateFetchParams(opts);
    const requestParams = {
      direct_scheduled_session: {
        persona_provider_id: personaProviderId,
        start_time: startTime.toISO({ includeOffset: true }),
        end_time: endTime.toISO({ includeOffset: true }),
        session_type: sessionType,
        client_goals: clientGoals,
        client_about: clientAbout,
        time_proposal: timeProposal
      }
    };
    // An omitted (or 0) location means the backend infers it from the client's state.
    if (locationId) {
      requestParams['direct_scheduled_session']['location_id'] = locationId;
    }
    if (personaClientId) {
      requestParams['persona_client_id'] = personaClientId;
    }
    if (clientReferralMemberId) {
      requestParams['client_referral_member_id'] = clientReferralMemberId;
    }
    return this.http.post<IAPIFetchResponse<IDirectScheduleSessionRequest>>(this.indexUrl, requestParams, params)
      .pipe(
        map((resp) => (resp?.data ? new DirectScheduleSessionRequest(resp.data) : null)),
        this.http.handleHttpError(opts.error)
      );
  }

  rescheduleSession$(
    sessionId: number,
    startTime?: DateTime | Date,
    endTime?: DateTime | Date,
    sessionType?: SessionType,
    locationId?: number
  ): Observable<DirectScheduleSessionRequest | null> {
    const requestBody: {
      direct_scheduled_session: {
        location_id?: number;
        start_time?: string;
        end_time?: string;
        session_type?: SessionType;
      };
    } = { direct_scheduled_session: {} };

    if (startTime) { requestBody.direct_scheduled_session.start_time = startTime instanceof DateTime ? startTime.toISO({ includeOffset: true }) : startTime.toISOString() }
    if (endTime) { requestBody.direct_scheduled_session.end_time = endTime instanceof DateTime ? endTime.toISO({ includeOffset: true }) : endTime.toISOString() }
    if (sessionType) { requestBody.direct_scheduled_session.session_type = sessionType }
    if (locationId) { requestBody.direct_scheduled_session.location_id = locationId }

    return this.http.patch<IAPIFetchResponse<IDirectScheduleSessionRequest>>(`${this.indexUrl}/${sessionId}`, requestBody)
      .pipe(
        map((resp) => (resp?.data ? new DirectScheduleSessionRequest(resp.data) : null)),
        this.http.handleHttpError()
      );
  }

  getSession(eventId: number, opts: Partial<IAPIListRequestOptions> = {}): Observable<DirectScheduleEvent | null> {
    const params = this.http.generateFetchParams(opts);
    const url = `${this.indexUrl}/${eventId}`;
    return this.http
      .get<IAPIFetchResponse<DirectScheduleEvent>>(url, params)
      .pipe(
        map((resp) => resp?.data ?? null),
        this.http.handleHttpError()
      );
  }
}
